import { useEffect, useState } from 'react'
import { colors } from '@chegg-ui/theme'
import { ChevronIcon } from '@chegg-ui/horizon-icons'
import { loggedInSelector, userRoleSelector } from '@chegg-wt/core/state/user/selectors'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearPapersForIndexDb,
  createPaper,
  getFirstPaper,
  readPaper,
} from '@chegg-wt/core-wa/utils/paperIndexedDb'
import {
  isPaperCheckInProgressSelector,
  isNewDocumentRequestedSelector,
  paperIdSelector,
  paperListSelector,
  paperTitleSelector,
  wordCountSelector,
  isLoggingOutSelector,
} from '@chegg-wt/core-wa/state/selectors'
import history from '@chegg-wt/core-wa/utils/history/history'
import {
  setIsLoggingOut,
  setPaperList,
  setPaperTitle,
} from '@chegg-wt/core-wa/state/actions'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { EMPTY_PAPER } from '@chegg-wt/core-wa/state/constants'
import { oneGraph } from '@chegg-wt/core/api/oneGraph'
import { useRouter } from 'next/router'
import { PaperStatus } from '@chegg-wt/core/globalTypesOneGraph'
import { PaperType } from '@chegg-wt/core-wa/types'
import { Editor } from '@tiptap/react'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import { getCurrentDateInMMDDYY } from '@chegg-wt/core-wa/utils/helpers'
import { UserRoles } from '@chegg-wt/core/constants/user'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { AIStarIcon } from '../../icons/AIStarIcon'
import { PaperList, WritingAssistantMenuItem } from '../styled'
import { onCloseQARightPanel } from '../../QuickActionRightPanel/functions/utils'
import { PapersListItem } from './PaperListItem'
import { NewDocumentButton } from './NewDocumentButton'
import { PaperListShimmer } from './PaperListShimmer'

export const PapersList = () => {
  const [isOpened, setOpened] = useState(true)
  const currentPaperId = useSelector(paperIdSelector)
  const isLoggedIn = useSelector(loggedInSelector)
  const userRole = useSelector(userRoleSelector)
  const isPaperCheckInProgress = useSelector(isPaperCheckInProgressSelector)
  const isNewDocumentRequested = useSelector(isNewDocumentRequestedSelector)
  const isLoggingOut = useSelector(isLoggingOutSelector)
  const paperList = useSelector(paperListSelector)
  const currentPaperTitle = useSelector(paperTitleSelector)
  const dispatch = useDispatch()
  const { toWritingAssistant } = useRoutesContext()
  const router = useRouter()
  const wordCount = useSelector(wordCountSelector)
  const editor: Editor = getFeatureEditor()
  const paperContent = {
    editorJSON: editor.getJSON(),
  }
  const isSignedInRole =
    userRole === UserRoles.FREE_USER || userRole === UserRoles.PRO_USER

  const handleClickPapersList = () => {
    // If a review is happening, CTA of new paper and any other papers are disabled
    if (isPaperCheckInProgress) return
    setOpened((isOpened) => !isOpened)
    dispatch(
      userClick({
        origin: isOpened ? origins.collapsePaperListClick : origins.expandPaperListClick,
      })
    )
  }

  // TODO: when paper in review, we can remove hover effects from button to imply list isDisabled.
  const handlePaperItemClick = async (id: string) => {
    // If a review is happening, CTA of new paper and any other papers are disabled
    if (isPaperCheckInProgress) return
    const updatedPaperList = [...paperList]

    if (currentPaperId !== id) {
      // If user has title/wordcount>0, we'll save the new paper for user.
      if (currentPaperId === null && (currentPaperTitle || wordCount > 0)) {
        let newTitle = currentPaperTitle

        if (!currentPaperTitle) {
          newTitle = `Paper - ${getCurrentDateInMMDDYY()}`
        }
        const newPaperId = await createPaper(
          newTitle,
          paperContent,
          {
            feedbackList: [],
            sentences: [],
            totalFeedbackCount: 0,
          },
          isLoggedIn
        )

        const updatedPaper = {
          ...updatedPaperList[0],
          title: newTitle,
          paperId: newPaperId,
        }
        updatedPaperList[0] = updatedPaper
        dispatch(setPaperList([...updatedPaperList]))
      }

      history.clear()
      dispatch(
        userClick({
          origin: origins.randomListPaperClick,
        })
      )
      onCloseQARightPanel(dispatch)
      dispatch(navigated(toWritingAssistant({ paperId: id })))
    }

    // Remove Draft when user clicks on a paper from list
    if (id) {
      const [first, ...restPaperList] = updatedPaperList
      if (!first.paperId) dispatch(setPaperList(restPaperList))
    }
  }

  // Guest users experience: load paper list from local indexedDb
  useEffect(() => {
    if (isLoggedIn || isSignedInRole) return

    async function fetchPaperFromIndexedDb() {
      if (!router.query.id) {
        const paperFromIndexedDb = await getFirstPaper()
        if (paperFromIndexedDb) {
          const paper: PaperType = {
            paperId: paperFromIndexedDb.id,
            title: paperFromIndexedDb.title,
            status: paperFromIndexedDb.status,
          }
          dispatch(navigated(toWritingAssistant({ paperId: paper.paperId })))
          dispatch(setPaperList([paper]))
        } else {
          dispatch(setPaperList([EMPTY_PAPER]))
        }
        dispatch(setIsLoggingOut(false))
      } else {
        const paperFromDb = await readPaper(router.query.id, false) // isLoggedIn will always be false
        if (paperFromDb) {
          const paper: PaperType = {
            paperId: paperFromDb.id,
            title: paperFromDb.title,
            status: PaperStatus.DRAFT,
          }
          dispatch(setPaperList([paper]))
          dispatch(setIsLoggingOut(false))
        }
      }
    }
    fetchPaperFromIndexedDb()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isSignedInRole])

  // Signed In users experience: load paper list from DB
  useEffect(() => {
    if (!isLoggedIn || isLoggingOut) return

    // Only call this function on refresh or url change, not on paperlist change
    async function fetchPapersFromDb() {
      if (!isNewDocumentRequested && (!paperList || paperList.length === 0)) {
        const { papers } = await oneGraph.getPaperList({})
        let newPaperId

        // When user has paperId in URL and data in indexDB
        if (papers.length === 0) {
          const paperData = await getFirstPaper()

          const newTitle =
            paperData?.title || currentPaperTitle || `Paper - ${getCurrentDateInMMDDYY()}`
          newPaperId = await createPaper(
            newTitle,
            paperContent,
            {
              feedbackList: [],
              sentences: [],
              totalFeedbackCount: 0,
            },
            isLoggedIn,
            paperData?.id || currentPaperId
          )

          const newPaper: PaperType = {
            title: newTitle,
            paperId: newPaperId,
            status: PaperStatus.DRAFT,
          }
          dispatch(setPaperList([newPaper]))
          if (newPaperId) clearPapersForIndexDb()
        } else {
          // CASE:  when user already has papers, but user also has paper in indexDB, we'll add the paper from indexDb to paperList
          let indexDbPaper
          const paperData = await getFirstPaper()

          if (paperData) {
            if (paperData) {
              const newTitle = paperData.title || `Paper - ${getCurrentDateInMMDDYY()}`
              newPaperId = await createPaper(
                newTitle,
                paperContent,
                {
                  feedbackList: [],
                  sentences: [],
                  totalFeedbackCount: 0,
                },
                isLoggedIn,
                paperData.id
              )

              const newPaper: PaperType = {
                title: newTitle,
                paperId: newPaperId,
                status: PaperStatus.DRAFT,
              }
              indexDbPaper = newPaper
              if (newPaperId) clearPapersForIndexDb()
            }
          }

          if (indexDbPaper) {
            dispatch(setPaperList([indexDbPaper, ...papers]))
          } else {
            dispatch(setPaperList(papers))
            newPaperId = papers[0]?.paperId
          }
        }
        // Open latest paper to user
        if (!router.query.id && newPaperId) {
          dispatch(navigated(toWritingAssistant({ paperId: newPaperId })))
        }
      }
    }

    fetchPapersFromDb()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  // Update paper title on Left Nav
  useEffect(() => {
    if (!currentPaperTitle) {
      return
    }

    const updatedPaperList = [...paperList]
    // update paper
    const paperIndex = updatedPaperList.findIndex(
      (paper) => paper.paperId === currentPaperId
    )
    if (paperIndex !== -1) {
      const updatedPaper = { ...updatedPaperList[paperIndex], title: currentPaperTitle }
      updatedPaperList[paperIndex] = updatedPaper
      dispatch(setPaperList(updatedPaperList))
    }
    dispatch(setPaperTitle(currentPaperTitle))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPaperTitle])

  return (
    <>
      <WritingAssistantMenuItem
        onClick={handleClickPapersList}
        leadingIcon={<AIStarIcon color={colors.cheggOrange} />}
        trailingIcon={
          <ChevronIcon
            pointing={isOpened ? 'up' : 'down'}
            color={colors.dark}
            size="xsmall"
            className="chevron"
          />
        }
      >
        Writing Assistant
      </WritingAssistantMenuItem>

      {isOpened &&
        (paperList.length > 0 ? (
          <PaperList>
            <NewDocumentButton
              isLoggedIn={isLoggedIn}
              isPaperCheckInProgress={isPaperCheckInProgress}
            />
            {paperList.map((item) => (
              <PapersListItem
                key={item.paperId || 'empty-paper'}
                title={item.title}
                status=""
                onClick={() => handlePaperItemClick(item.paperId)}
                isActive={currentPaperId === item.paperId}
                isPaperCheckInProgress={isPaperCheckInProgress}
              />
            ))}
          </PaperList>
        ) : (
          <PaperListShimmer />
        ))}
    </>
  )
}
